import React from 'react';
import { formatCurrency, getLeadLifecycleStyle, parseKarachiDateTimeToISOString } from '../utils/helpers';
import type { Lead } from '../types';
import { Badge } from './common';
import { availabilityAPI, leadsAPI, followUpsAPI } from '../utils/api-service';
import { Modal, Button } from './common';
import { useDataStore } from '../context/store';

interface LeadCardProps {
  lead: Lead;
  onClick?: () => void;
}

export const LeadCard: React.FC<LeadCardProps> = ({ lead, onClick }) => {
  const [tab, setTab] = React.useState<'overview' | 'availability'>('overview');
  const [availability, setAvailability] = React.useState<any>(null);
  const [health, setHealth] = React.useState<{ score: number; health: 'red' | 'yellow' | 'green' } | null>(null);
  const [now, setNow] = React.useState(Date.now());
  const [showReminderModal, setShowReminderModal] = React.useState(false);
  const [reminderTitle, setReminderTitle] = React.useState('');
  const [reminderWhen, setReminderWhen] = React.useState('');
  const [reminderNote, setReminderNote] = React.useState('');
  const [showHotelModal, setShowHotelModal] = React.useState(false);
  const [hotelForm, setHotelForm] = React.useState({
    hotelName: lead.hotelInfo?.hotelName || '',
    roomType: lead.hotelInfo?.roomType || '',
    roomPrice: lead.hotelInfo?.roomPrice || 0
  });
  const notifications = useDataStore((s) => s.notifications);

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, []);

  React.useEffect(() => {
    const loadMeta = async () => {
      try {
        const availabilityResponse = await availabilityAPI.getByLeadId(lead.id);
        const healthResponse = await leadsAPI.getHealth(lead.id);
        
        setAvailability(availabilityResponse.data);
        setHealth(healthResponse.data);
      } catch {
        setAvailability(null);
      }
    };
    loadMeta();
  }, [lead.id]);

  const holdExpiryText = React.useMemo(() => {
    if (!availability?.hold_expiry) return null;
    const msLeft = new Date(availability.hold_expiry).getTime() - now;
    if (msLeft <= 0) return 'Hold expired';
    const hours = Math.floor(msLeft / (1000 * 60 * 60));
    const minutes = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m left`;
  }, [availability?.hold_expiry, now]);

  const followUps = useDataStore((s) => s.followUps);
  const nextFollowUp = React.useMemo(() => {
    const pending = followUps
      .filter((item) => String(item.leadId) === String(lead.id))
      .filter((item) => item.status !== 'completed' && item.status !== 'canceled')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    return pending[0] || null;
  }, [followUps, lead.id]);

  const followUpBadge = React.useMemo(() => {
    if (!nextFollowUp) return null;
    const dueAt = new Date(nextFollowUp.dueDate).getTime();
    const diffMs = dueAt - Date.now();
    if (Number.isNaN(dueAt)) return null;

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    let label = 'Follow-up scheduled';
    if (diffMs <= 0) {
      label = 'Follow-up overdue';
    } else if (days > 0) {
      label = `Follow-up scheduled • in ${days}d ${hours}h`;
    } else if (hours > 0) {
      label = `Follow-up scheduled • in ${hours}h ${minutes}m`;
    } else {
      label = `Follow-up scheduled • in ${minutes}m`;
    }
    return label;
  }, [nextFollowUp]);

  const healthColor = health?.health === 'green'
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : health?.health === 'yellow'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';

  const createdAtDate = React.useMemo(() => {
    const value = lead.createdAt;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(value);
  }, [lead.createdAt]);

  // Keep updatedAtDate calculation but don't display it
  // const updatedAtDate = React.useMemo(() => {
  //   const value = lead.updatedAt || lead.createdAt;
  //   if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
  //     const [year, month, day] = value.split('-').map(Number);
  //     return new Date(year, month - 1, day);
  //   }
  //   return new Date(value);
  // }, [lead.updatedAt, lead.createdAt]);

  const lifecycle = getLeadLifecycleStyle(lead);

  return (
    <div
      className="lead-card group relative cursor-pointer"
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('button, input, select, textarea, a')) {
          return;
        }
        onClick?.();
      }}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && onClick) {
          event.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Unread notification badge */}
      {notifications.some((n: any) => !n.is_read && n.leadId === lead.id) && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500 rounded-full text-white text-xs font-bold shadow-md">
            !</span>
        </div>
      )}

      {/* Header Section */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-slate-900 truncate group-hover:text-amber-700 transition-colors">
              {lead.clientName}
            </h3>
          </div>
          <div className="flex-shrink-0">
            <Badge color={lifecycle.badge}>
              <span className="text-xs font-semibold">{lifecycle.label}</span>
            </Badge>
          </div>
        </div>

        {/* Key Info Row */}
        <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
          <span>📍</span>
          <span className="truncate font-medium">{lead.destination || 'TBD'}</span>
          {lead.travelDates?.from && (
            <span className="text-slate-500">•</span>
          )}
          {lead.travelDates?.from && (
            <span className="text-slate-500">
              {new Date(lead.travelDates.from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        {/* Secondary Info */}
        <div className="flex items-center gap-2 flex-wrap">
          {health && (
            <Badge color={healthColor}>
              <span className="text-xs font-semibold">Score: {health.score}%</span>
            </Badge>
          )}
          {(lead as any).islamabadStay && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
              (lead as any).islamabadStay === 'yes' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-slate-100 text-slate-700'
            }`}>
              {(lead as any).islamabadStay === 'yes' ? '✓ Islamabad' : '✗ No Islamabad'}
            </span>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-2 mb-4 pb-4 border-b border-slate-200">
        <div className="min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Budget</p>
          <p className="font-bold text-sm text-slate-900">{formatCurrency(lead.budget)}</p>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Tour Type</p>
          <p className="font-bold text-sm text-slate-900 capitalize">
            {lead.tourType ? lead.tourType.charAt(0).toUpperCase() + lead.tourType.slice(1) : '—'}
          </p>
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4 pb-4 border-b border-slate-200">
        <div className="text-xs">
          <p className="text-slate-500 font-semibold mb-1">📧 Email</p>
          <p className="font-medium text-slate-900 break-all text-xs">{lead.email}</p>
        </div>
        <div className="text-xs">
          <p className="text-slate-500 font-semibold mb-1">📞 Phone</p>
          <p className="font-medium text-slate-900 text-xs">{lead.phone}</p>
        </div>
      </div>

      {/* Timeline Info */}
      <div className="flex flex-col gap-1 text-xs text-slate-500 mb-3">
        <div className="flex justify-between">
          <span>Created:</span>
          <span className="font-medium text-slate-700">
            {createdAtDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        {followUpBadge && (
          <div className="flex justify-between bg-blue-50 p-2 rounded-md text-blue-700 font-semibold">
            <span>⏰ Follow-up</span>
            <span>{followUpBadge}</span>
          </div>
        )}
      </div>

      {/* Hotel Info */}
      {lead.hotelInfo && (
        <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-900 mb-1">🏨 Hotel Booked</p>
          <p className="text-xs font-medium text-slate-900">{lead.hotelInfo.hotelName}</p>
          <p className="text-xs text-slate-600">{formatCurrency(lead.hotelInfo.roomPrice)}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-3 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab('overview')}
          className={`text-xs px-3 py-2 font-semibold border-b-2 transition-colors ${
            tab === 'overview'
              ? 'border-amber-500 text-amber-700 bg-amber-50'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Overview
        </button>
        <button
          type="button"
          onClick={() => setTab('availability')}
          className={`text-xs px-3 py-2 font-semibold border-b-2 transition-colors ${
            tab === 'availability'
              ? 'border-amber-500 text-amber-700 bg-amber-50'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Availability
        </button>
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="mb-3 text-xs space-y-2 max-h-[200px] overflow-y-auto">
          {lead.agentRemarks && (
            <div className="bg-slate-50 p-2 rounded border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Notes</p>
              <p className="text-slate-600">{lead.agentRemarks}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-slate-500 font-semibold">Source</p>
              <p className="font-medium text-slate-700 capitalize">{lead.source || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 font-semibold">Status</p>
              <p className="font-medium text-slate-700 capitalize">{lead.status || '—'}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'availability' && (
        <div className="mb-3 text-xs space-y-2">
          <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded">
            <div className="text-center">
              <p className="text-slate-600 font-semibold block mb-1">🏨</p>
              <p className="text-slate-700 capitalize font-medium text-xs">
                {availability?.hotel_status || 'N/A'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-slate-600 font-semibold block mb-1">🚗</p>
              <p className="text-slate-700 capitalize font-medium text-xs">
                {availability?.transport_status || 'N/A'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-slate-600 font-semibold block mb-1">👤</p>
              <p className="text-slate-700 capitalize font-medium text-xs">
                {availability?.guide_status || 'N/A'}
              </p>
            </div>
          </div>
          {holdExpiryText && (
            <div className={`text-xs font-semibold p-2 rounded ${
              holdExpiryText === 'Hold expired'
                ? 'bg-red-50 text-red-700'
                : 'bg-amber-50 text-amber-700'
            }`}>
              ⏱️ Hold: {holdExpiryText}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClick?.();
          }}
          className="flex-1 text-xs px-2 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold transition-colors"
        >
          Open
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowReminderModal(true);
          }}
          className="flex-1 text-xs px-2 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold transition-colors"
        >
          Follow-up
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowHotelModal(true);
          }}
          className="flex-1 text-xs px-2 py-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100 font-semibold transition-colors"
        >
          Hotel
        </button>
      </div>

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 w-full max-w-md">
            <h3 className="font-semibold mb-2">Schedule Reminder</h3>
            <label className="block text-xs text-slate-600">Title</label>
            <input value={reminderTitle} onChange={(e) => setReminderTitle(e.target.value)} className="w-full p-2 rounded mb-2 bg-slate-50 dark:bg-slate-700" />
            <label className="block text-xs text-slate-600">Due (local datetime)</label>
            <input type="datetime-local" value={reminderWhen} onChange={(e) => setReminderWhen(e.target.value)} className="w-full p-2 rounded mb-4 bg-slate-50 dark:bg-slate-700" />
            <label className="block text-xs text-slate-600">Note</label>
            <textarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)} className="w-full p-2 rounded mb-4 bg-slate-50 dark:bg-slate-700" rows={4} placeholder="Add a note for this follow-up" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReminderModal(false)} className="px-3 py-1 rounded bg-slate-200">Cancel</button>
              <button onClick={async () => {
                if (!reminderTitle || reminderTitle.trim().length < 2) {
                  alert('Please enter a title for the reminder (at least 2 characters).');
                  return;
                }
                if (!reminderWhen) {
                  alert('Please choose a due date/time.');
                  return;
                }
                if (!reminderNote || reminderNote.trim().length < 2) {
                  alert('Please enter a note for the reminder (at least 2 characters).');
                  return;
                }
                try {
                  const iso = parseKarachiDateTimeToISOString(reminderWhen);
                  await followUpsAPI.create({ leadId: lead.id, title: reminderTitle, description: reminderNote.trim(), dueDate: iso, assignedTo: lead.agentId });
                  setShowReminderModal(false);
                  alert('Reminder scheduled');
                } catch (err: any) {
                  const msg = err?.response?.data?.message || err?.message || 'Failed to create reminder';
                  alert(msg);
                }
              }} className="px-3 py-1 rounded bg-primary-500 text-white">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Hotel Modal */}
      <Modal
        isOpen={showHotelModal}
        onClose={() => setShowHotelModal(false)}
        title="Edit Hotel Details"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowHotelModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={async () => {
              try {
                await leadsAPI.update(lead.id, { hotelInfo: hotelForm });
                setShowHotelModal(false);
                window.location.reload();
              } catch (e) {
                alert('Failed to update hotel details');
              }
            }}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Hotel Name</label>
            <input className="input-field" value={hotelForm.hotelName} onChange={(e) => setHotelForm((s) => ({ ...s, hotelName: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm mb-1">Room Type</label>
            <input className="input-field" value={hotelForm.roomType} onChange={(e) => setHotelForm((s) => ({ ...s, roomType: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm mb-1">Room Price</label>
            <input type="number" className="input-field" value={hotelForm.roomPrice} onChange={(e) => setHotelForm((s) => ({ ...s, roomPrice: Number(e.target.value) }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

interface LeadListProps {
  leads: Lead[];
  onSelectLead?: (lead: Lead) => void;
}

export const LeadList: React.FC<LeadListProps> = ({ leads, onSelectLead }) => {
  if (leads.length === 0) {
    return (
      <div className="card text-center py-8">
        <p className="text-slate-600 dark:text-slate-400">No leads found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {leads.map((lead) => (
        <LeadCard key={lead.id} lead={lead} onClick={() => onSelectLead?.(lead)} />
      ))}
    </div>
  );
};
